import React, { useCallback, useEffect, useState } from "react";
import cn from "classnames";
import styles from "./SplitEditor.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAIManager } from "../../../reports/hooks/useAIManager";
import { withTooltip } from "@/common/lib/renderers";
import { IEditor } from "./interfaces";
import FroalaEditor from "react-froala-wysiwyg";
import { FROALA_KEY } from "./configs";
import { getAskCaitButtonHtml } from "./askCaitButtonHtml";
import { faSparkles } from "@fortawesome/pro-solid-svg-icons";
const froalaMiniConfig = {
  key: FROALA_KEY,
  toolbarInline: false,
  toolbarButtons: []
};

// Undo/Redo hook that updates the parent's active AI message.
const useUndoRedo = (setActiveAIMessage: (msg: string) => void) => {
  const { getCompletedMessages } = useAIManager();
  const completedMessages = getCompletedMessages();
  const [currentIndex, setCurrentIndex] = useState(
    completedMessages.length > 0 ? completedMessages.length - 1 : -1
  );

  useEffect(() => {
    if (completedMessages.length > 0) {
      setCurrentIndex(completedMessages.length - 1);
    } else {
      setCurrentIndex(-1);
    }
  }, [completedMessages]);

  const processMessageText = (messageText: string): string => {
    // Remove code block markers safely.
    return messageText.replace("```html", "").replace("```", "").split("\n\n")[1];
  };

  const undo = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      const message = completedMessages[newIndex];
      const messageText = message?.chunk?.text;
      if (messageText) {
        setActiveAIMessage(processMessageText(messageText));
      }
    }
  }, [currentIndex, completedMessages, setActiveAIMessage]);

  const redo = useCallback(() => {
    if (currentIndex < completedMessages.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      const message = completedMessages[newIndex];
      const messageText = message?.chunk?.text;
      if (messageText) {
        setActiveAIMessage(processMessageText(messageText));
      }
    }
  }, [currentIndex, completedMessages, setActiveAIMessage]);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < completedMessages.length - 1;

  return { undo, redo, canUndo, canRedo };
};

interface SplitEditorProps {
  onClose: () => void
  className?: string
  editor: IEditor
  aiVars?: Record<string, string>
}

export let aiRequestInitialized: boolean = false;

export const setAiRequestInitialized = (value: boolean): void => {
  aiRequestInitialized = value;
};

// useAIContent computes the messages based on the current message stream.
const useAIContent = (aiVars: Record<string, string>,
  editor: IEditor) => {
  const { isProcessing, messages, sendAIRequest, clearMessages, totalClearMessages, getErrorMessages } = useAIManager();
  const [initialRequestSent, setInitialRequestSent] = useState(false);

  const promptText = editor.html.get();
  let initialAndEmptyRequest: boolean;
  if (!aiRequestInitialized && (!promptText || promptText.trim().length < 5)) {
    initialAndEmptyRequest = true;
  } else {
    initialAndEmptyRequest = false;
  }

  const processMessages = () => {
    if (messages.length === 0) {
      return { aiMessages: "", mainMessages: "" };
    }
    const lastMessage = messages[messages.length - 1];
    let allMessages = messages.map(x => x.chunk?.text || "").join("");
    if (lastMessage?.status === "complete") {
      allMessages = lastMessage.chunk?.text?.replace("```html", "").replace("```", "") || "";
    }

    const [aiMessages, mainMessages] = allMessages.split("\n\n");

    return { aiMessages, mainMessages };
  };

  // Wrapper for sending the AI request.
  const _sendAIRequest = () => {
    // we dont run ai on initialize with empty content
    if (!aiRequestInitialized && initialAndEmptyRequest) {
      aiRequestInitialized = true;
      return;
    }

    const params = {
      prompt: aiVars.prompt,
      text: promptText
    };
    const requestType = "analyze_data";
    clearMessages();
    sendAIRequest(requestType, params);
    setInitialRequestSent(true);
  };

  let { aiMessages, mainMessages } = processMessages();

  if (!aiRequestInitialized && initialAndEmptyRequest) {
    aiMessages = "Press a button to generate text.";
  }

  return {
    isProcessing,
    mainMessages,
    aiMessages,
    errorMessages: getErrorMessages(),
    clearMessages,
    totalClearMessages,
    initialRequestSent,
    sendAIRequest: _sendAIRequest
  };
};

const AIMessages: React.FC<{ isProcessing: boolean, aiMessages: string }> = ({ isProcessing, aiMessages }) => {
  let text: string | null = null;
  if (isProcessing) {
    text = "Processing...";
  } else if (aiMessages) {
    text = aiMessages;
  }
  if (!text) {
    return null;
  }
  return <strong>{text}</strong>;
};

interface SplitEditorContentProps {
  editor: IEditor
  aiVars: Record<string, string>
  activeAIMessage: string
  clearActiveAIMessage: () => void
}

const SplitEditorContent: React.FC<SplitEditorContentProps> = ({
  editor,
  aiVars,
  activeAIMessage,
  clearActiveAIMessage
}) => {
  const { isProcessing, mainMessages, aiMessages, sendAIRequest, errorMessages } = useAIContent(aiVars, editor);
  const [splitEditorInstance, setSplitEditorInstance] = useState<IEditor>();

  let messageToUse = activeAIMessage || mainMessages;

  // Send the AI request when the component is mounted.
  useEffect(sendAIRequest, []);

  const applyAction = useCallback(async() => {
    if (splitEditorInstance) {
      messageToUse = splitEditorInstance.html.get();
    }
    if (editor && messageToUse) {
      try {
        editor.html.set(messageToUse);
        return editor;
      } catch (error) {
        console.error("---> applyAction failed <--- | ", error);
        return undefined;
      }
    }

    return undefined;
  }, [editor, messageToUse, splitEditorInstance]);

  const copyAction = useCallback(async() => {
    if (!splitEditorInstance) {
      return false;
    }

    const rawHtml = splitEditorInstance.html.get() || "";

    if (!rawHtml) {
      return false;
    }

    const cleanedHtml = rawHtml.replaceAll("</li>\n  <li>", "</li><li>");
    // for pasting in non-supporting html type envs
    const cleanedText = cleanedHtml.replaceAll("</li><li>", "\n").replaceAll(/<[^>]+>/g, ""); ;

    const blobMap: Record<string, Blob> = {
      "text/plain": new Blob([cleanedText], { type: "text/plain" }),
      "text/html": new Blob([cleanedHtml], { type: "text/html" })
    };

    try {
      const item = new ClipboardItem(blobMap);
      await navigator.clipboard.write([item]);
      return true;
    } catch (error) {
      console.error("copyAction failed:", error);
      return false;
    }
  }, [splitEditorInstance]);

  return (
    <div className={styles.editor__content}>
      <AIMessages isProcessing={isProcessing} aiMessages={aiMessages} />
      {errorMessages.length > 0 && (
        <div className={styles.editor__error}>
          {errorMessages.map(x => x.message).join("\n")}
        </div>
      )}
      <div>
        {messageToUse
          ? (
            <FroalaEditor
              model={messageToUse}
              config={{
                ...froalaMiniConfig,
                events: {
                  initialized: function() {
                    // @ts-expect-error - 'this' in Froala's context is the editor instance
                    setSplitEditorInstance(this);
                  }
                }
              }}
            />
            )
          : (
            <span>&nbsp;</span>
            )}
      </div>
      <SplitEditorButtons
        sendAIRequest={sendAIRequest}
        applyAction={applyAction}
        copyAction={copyAction}
        clearActiveAIMessage={clearActiveAIMessage}
      />
    </div>
  );
};

interface SplitEditorButtonsProps {
  sendAIRequest: () => void
  applyAction: () => Promise<IEditor | undefined>
  copyAction: () => Promise<boolean>
  clearActiveAIMessage: () => void
}

interface Notification {
  message: string
  type: "apply" | "copy" | ""
}
const SplitEditorButtons: React.FC<SplitEditorButtonsProps> = ({
  sendAIRequest,
  applyAction,
  copyAction,
  clearActiveAIMessage
}) => {
  const [notification, setNotification] = useState<Notification | null>(null);
  const notificationLiveSeconds = 3 * 1000;

  const handleApply = async() => {
    const editor = await applyAction();
    if (editor) {
      editor.events.trigger("contentChanged");
      setNotification({
        message: "AI-generated text inserted successfully.",
        type: "apply"
      });
      setTimeout(() => setNotification(null), notificationLiveSeconds);
    }
  };

  const handleCopy = async() => {
    const success = await copyAction();
    if (success) {
      setNotification({
        message: "AI-generated text copied to clipboard.",
        type: "copy"
      });
      setTimeout(() => setNotification(null), notificationLiveSeconds);
    }
  };

  return (
    <div className={styles.editor__buttons_wrapper}>
      <div className={styles.editor__buttons}>
        {withTooltip(
          "Generate text",
          <FontAwesomeIcon
            icon={"arrow-rotate-right"}
            className={styles.icon}
            data-clickable-btn='send-ai-request'
            onClick={() => {
              // always send request by btn click
              aiRequestInitialized = true;
              clearActiveAIMessage();
              sendAIRequest();
            }}
          />
        )}
        {withTooltip(
          "Apply",
          <FontAwesomeIcon icon={["far", "arrow-left-to-line"]}
            className={styles.icon}
            onClick={handleApply} />
        )}
        {withTooltip(
          "Copy",
          <FontAwesomeIcon icon={["far", "copy"]}
            className={styles.icon}
            onClick={handleCopy} />
        )}
      </div>
      {notification &&
        <div
          className={cn(styles.notificationAI, {
            [styles.apply]: notification.type === "apply",
            [styles.copy]: notification.type === "copy"
          })}
        >
          {notification.message}
        </div>}
    </div>
  );
};

const SplitEditorHeader: React.FC<{
  onClose: () => void
  onUndo: () => void
  onRedo: () => void
  canUndo: boolean
  canRedo: boolean
}> = ({ onClose, onUndo, onRedo, canUndo, canRedo }) => {
  return (
    <div className={cn(styles.editor__header, "fr-toolbar fr-desktop")}>
      <div className="fr-btn-grp fr-float-left">
        <button className={cn("fr-command", "fr-btn", styles.askCaitIcon)}
                onClick={onClose}
                data-cmd="askCait"
                dangerouslySetInnerHTML={{
                  __html:
                      getAskCaitButtonHtml({
                        className: styles.askCaitIcon,
                        path: faSparkles.icon[4]
                      })
                }}>
        </button>
      </div>
      <div className="fr-btn-grp fr-float-right ms-layer-display-none">
        <button
          className={cn("fr-command", "fr-btn", { "fr-disabled": !canUndo })}
          disabled={!canUndo}
          aria-disabled={!canUndo}
          onClick={(e) => {
            onUndo();
            e.currentTarget.blur();
          }}
        >
          {withTooltip("Previous Version", <FontAwesomeIcon icon={["fas", "arrow-left"]} />)}
        </button>
        <button
          className={cn("fr-command", "fr-btn", { "fr-disabled": !canRedo })}
          disabled={!canRedo}
          aria-disabled={!canRedo}
          onClick={(e) => {
            onRedo();
            e.currentTarget.blur();
          }}
        >
          {withTooltip("Next Version", <FontAwesomeIcon icon={["fas", "arrow-right"]} />)}
        </button>
        <button className="fr-command fr-btn" onClick={onClose}>
          {withTooltip("Close", <FontAwesomeIcon icon={["fas", "close"]} />)}
        </button>
      </div>
    </div>
  );
};

const SplitEditor: React.FC<SplitEditorProps> = ({ onClose, className, editor, aiVars }) => {
  // Parent holds the active AI message state.
  const [activeAIMessage, setActiveAIMessage] = useState("");
  const { undo, redo, canUndo, canRedo } = useUndoRedo(setActiveAIMessage);

  const clearActiveAIMessage = useCallback(() => {
    setActiveAIMessage("");
  }, []);

  return (
    <div className={cn(styles.editor, className)}>
      <SplitEditorHeader onClose={onClose} onUndo={undo} onRedo={redo} canUndo={canUndo} canRedo={canRedo} />
      <div className={styles.editor__content}>
        <SplitEditorContent
          editor={editor}
          aiVars={aiVars || {}}
          activeAIMessage={activeAIMessage}
          clearActiveAIMessage={clearActiveAIMessage}
        />
      </div>
    </div>
  );
};

export default SplitEditor;
